import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import AppLayout from './AppLayout';
import Button from './ui/button';
import EmptyState from './ui/empty-state';
import AddStudentForm from '../AddStudentForm.jsx';
import { useStudents } from '../lib/useStudents';
import { useDataStore } from '../lib/stores/dataStore';
import { Icons } from '../lib/Icons';
import { resolveStudentSearch } from '../lib/studentSearch';

// Helper to format Firestore timestamps or date strings
const formatDate = (value) => {
  if (!value) return null;
  // Handle Firestore Timestamp objects
  if (value && typeof value === 'object' && 'seconds' in value) {
    return new Date(value.seconds * 1000).toLocaleDateString();
  }
  // Handle Date objects
  if (value instanceof Date) {
    return value.toLocaleDateString();
  }
  // Handle ISO strings or other date strings
  if (typeof value === 'string') {
    return value;
  }
  return null;
};

const StudentOrganizationViewer = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { students, loading: studentsLoading } = useStudents({ orderByLastName: true });
  const [expandedYearGroups, setExpandedYearGroups] = useState(new Set());
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterEHCP, setFilterEHCP] = useState('all');
  const [showAddStudentForm, setShowAddStudentForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const yearGroupListRef = useRef(null);
  const interventionsByStudent = useDataStore((s) => s.interventionsByStudent);
  const interventionsLoading = useDataStore((s) => s.interventionsLoading);
  const subscribeInterventions = useDataStore((s) => s.subscribeInterventions);

  // Debug logging
  console.log('👥 StudentOrganizationViewer render:', {
    studentsCount: students?.length || 0,
    studentsLoading,
    interventionsLoading,
    filterPhase,
    filterEHCP,
    searchQuery
  });

  useEffect(() => {
    const unsub = subscribeInterventions();
    return () => { try { unsub && unsub(); } catch { /* noop */ } };
  }, []); // Remove subscribeInterventions from dependencies to prevent infinite re-renders

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    setSearchQuery(urlSearch);
  }, [searchParams]);

  useEffect(() => {
    const urlSearch = searchParams.get('search') || '';
    if (!urlSearch.trim() || studentsLoading || !students.length) return;
    const match = resolveStudentSearch(students, urlSearch);
    if (match?.id) {
      navigate(`/student/${match.id}`, { replace: true });
    }
  }, [searchParams, students, studentsLoading, navigate]);

  const updateSearchQuery = (value) => {
    setSearchQuery(value);
    if (value.trim()) {
      setSearchParams({ search: value.trim() }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  // Filter students based on current filters
  const filteredStudents = students.filter(student => {
    if (filterPhase !== 'all' && student.phase !== filterPhase) return false;
    if (filterEHCP !== 'all') {
      if (filterEHCP === 'with' && !student.hasEHCP) return false;
      if (filterEHCP === 'without' && student.hasEHCP) return false;
    }
    if (searchQuery.trim() !== '') {
      const q = searchQuery.toLowerCase();
      const fullName = `${student.firstName || ''} ${student.lastName || ''}`.toLowerCase();
      const yearGroup = (student.yearGroup || '').toLowerCase();
      const matchesNeed = (student.specialNeeds || []).some((need) =>
        need.toLowerCase().includes(q)
      );
      if (!fullName.includes(q) && !yearGroup.includes(q) && !matchesNeed) return false;
    }
    return true;
  });

  // Debug filtering
  console.log('🔍 StudentOrganizationViewer filtering:', {
    originalCount: students?.length || 0,
    filteredCount: filteredStudents?.length || 0,
    filters: { filterPhase, filterEHCP, searchQuery }
  });

  // Group students by year group
  const studentsByYearGroup = filteredStudents.reduce((acc, student) => {
    if (!acc[student.yearGroup]) {
      acc[student.yearGroup] = [];
    }
    acc[student.yearGroup].push(student);
    return acc;
  }, {});

  const effectiveExpandedYearGroups = searchQuery.trim()
    ? new Set(Object.keys(studentsByYearGroup))
    : expandedYearGroups;

  // Debug year group organization
  console.log('📊 StudentOrganizationViewer year groups:', {
    yearGroups: Object.keys(studentsByYearGroup),
    yearGroupCounts: Object.entries(studentsByYearGroup).map(([group, students]) => ({
      [group]: students.length
    }))
  });



  // Toggle year group expansion
  const toggleYearGroup = (yearGroup) => {
    const newExpanded = new Set(expandedYearGroups);
    if (newExpanded.has(yearGroup)) {
      newExpanded.delete(yearGroup);
    } else {
      newExpanded.add(yearGroup);
    }
    setExpandedYearGroups(newExpanded);
  };



  // Get EHCP badge
  const getEHCPBadge = (hasEHCP) => {
    return hasEHCP ? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        EHCP
      </span>
    ) : (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        No EHCP
      </span>
    );
  };

  // Get phase badge
  const getPhaseBadge = (phase) => {
    if (!phase || typeof phase !== 'string') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Set
        </span>
      );
    }
    const isPrimary = phase.toLowerCase() === 'primary';
    const label = phase.charAt(0).toUpperCase() + phase.slice(1);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isPrimary ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
        {label}
      </span>
    );
  };

  // Get academic level badge
  const getAcademicLevelBadge = (level) => {
    // Handle undefined, null, or non-string level
    if (!level || typeof level !== 'string') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          Not Set
        </span>
      );
    }

    const colors = {
      'working below': 'bg-yellow-100 text-yellow-800',
      'working at': 'bg-green-100 text-green-800',
      'working above': 'bg-blue-100 text-blue-800'
    };
    
    // Default color for unknown levels
    const colorClass = colors[level] || 'bg-gray-100 text-gray-800';
    
    // Safe string replacement
    const displayText = level.includes('working ') ? level.replace('working ', '') : level;
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {displayText}
      </span>
    );
  };

  const loading = studentsLoading || interventionsLoading;
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-xl">
                  <Icons.Users className="h-7 w-7 text-blue-600" />
                </div>
                Student Organization
              </h1>
              <p className="text-gray-600 mt-2">
                View students organized by year group and class. Click on year groups or classes to reveal student details.
              </p>
            </div>
            <Button onClick={() => setShowAddStudentForm(true)}>
              <Icons.Plus className="w-5 h-5 mr-2" />
              Add New Student
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900">Total Students</h3>
            <p className="text-3xl font-bold text-blue-600">{filteredStudents.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900">Year Groups</h3>
            <p className="text-3xl font-bold text-green-600">{Object.keys(studentsByYearGroup).length}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              setFilterEHCP('with');
              setExpandedYearGroups(new Set(Object.keys(studentsByYearGroup)));
              setTimeout(() => yearGroupListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
            }}
            className="bg-white rounded-lg shadow p-6 text-left hover:shadow-md hover:bg-gray-50 transition-all focus:outline-none focus:ring-2 focus:ring-red-200"
            aria-label="Show all students with EHCP"
          >
            <h3 className="text-lg font-semibold text-gray-900">Students with EHCP</h3>
            <p className="text-3xl font-bold text-red-600">
              {filteredStudents.filter(s => s.hasEHCP).length}
            </p>
            <p className="mt-1 text-xs text-gray-500">Click to view list</p>
          </button>
        </div>

        {/* Quick Search */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <label htmlFor="student-search" className="block text-sm font-medium text-gray-700 mb-2">
            Search students
          </label>
          <div className="relative">
            <input
              id="student-search"
              type="text"
              value={searchQuery}
              onChange={(e) => updateSearchQuery(e.target.value)}
              placeholder="Type a name, e.g. Jane Doe"
              className="w-full border border-gray-300 rounded-md pl-10 pr-10 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            {searchQuery && (
              <button
                type="button"
                onClick={() => updateSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {searchQuery && (
            <div className="mt-2 text-sm text-gray-500">
              Showing {filteredStudents.length} result{filteredStudents.length === 1 ? '' : 's'} for "{searchQuery}"
            </div>
          )}
        </div>

        {/* Main Content Container */}
        <div className="space-y-8">
          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phase</label>
              <select
                value={filterPhase}
                onChange={(e) => setFilterPhase(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="all">All Phases</option>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">EHCP Status</label>
              <select
                value={filterEHCP}
                onChange={(e) => setFilterEHCP(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="all">All Students</option>
                <option value="with">With EHCP</option>
                <option value="without">Without EHCP</option>
              </select>
            </div>
          </div>
        </div>

        {/* Year Group Organization */}
        <div ref={yearGroupListRef} className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            {Object.keys(studentsByYearGroup).length === 0 && (
              <>
                {console.log('🚨 Empty state showing - no year groups found')}
                <EmptyState
                  title="No students found"
                  description={searchQuery ? 'Try clearing your search or adjusting filters.' : 'Add your first student to get started.'}
                  actionLabel={!searchQuery ? 'Add Student' : undefined}
                  onAction={!searchQuery ? () => setShowAddStudentForm(true) : undefined}
                />
              </>
            )}
            {console.log('🎯 Rendering year groups:', Object.keys(studentsByYearGroup).length > 0 ? 'YES' : 'NO')}
            {Object.entries(studentsByYearGroup)
              .sort(([a], [b]) => {
                // Sort year groups logically with safe fallback
                const yearOrder = {
                  'Reception': 0,
                  'Year 1': 1,
                  'Year 2': 2,
                  'Year 3': 3,
                  'Year 4': 4,
                  'Year 5': 5,
                  'Year 6': 6,
                  'Year 7': 7,
                  'Year 8': 8,
                  'Year 9': 9,
                  'Year 10': 10,
                  'Year 11': 11
                };
                const aOrder = a in yearOrder ? yearOrder[a] : 999;
                const bOrder = b in yearOrder ? yearOrder[b] : 999;
                if (aOrder !== bOrder) return aOrder - bOrder;
                return String(a).localeCompare(String(b));
              })
              .map(([yearGroup, students]) => (
                <div key={yearGroup} className="border border-gray-200 rounded-lg">
                  <button
                    onClick={() => toggleYearGroup(yearGroup)}
                    className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors duration-200 rounded-t-lg"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <h3 className="text-lg font-semibold text-gray-900">{yearGroup}</h3>
                        <span className="text-sm text-gray-500">{students.length} students</span>
                        {getPhaseBadge(students[0]?.phase)}
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">
                          {students.filter(s => s.hasEHCP).length} with EHCP
                        </span>
                        <svg
                          className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 ${
                            effectiveExpandedYearGroups.has(yearGroup) ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                  
                  {effectiveExpandedYearGroups.has(yearGroup) && (
                    <div className="px-6 py-4 bg-white rounded-b-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {students
                          .sort((a, b) => a.lastName.localeCompare(b.lastName))
                          .map((student) => (
                            <div key={student.id} className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200">
                              <div className="flex items-start justify-between mb-2">
                                <Link 
                                  to={`/student/${student.id}`}
                                  className="font-medium text-gray-900 hover:text-blue-600 transition-colors duration-200"
                                >
                                  {student.firstName} {student.lastName}
                                </Link>
                                {getEHCPBadge(student.hasEHCP)}
                              </div>
                              <div className="space-y-1 text-sm text-gray-600">
                                <div>Date of Birth: {formatDate(student.dateOfBirth) || 'Not set'}</div>
                                <div>Academic Level: {getAcademicLevelBadge(student.academicLevel)}</div>
                                <div>Attendance: {student.attendance || 0}%</div>
                                <div>Special Needs: {(student.specialNeeds || []).join(', ') || 'None listed'}</div>
                                <div>Intervention Groups: {(interventionsByStudent[student.id]?.length || 0)}</div>
                                {interventionsByStudent[student.id]?.length > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {interventionsByStudent[student.id]
                                      .slice(0, 2)
                                      .map((i) => i.name || i.type)
                                      .join(', ')}
                                    {interventionsByStudent[student.id].length > 2 ? '…' : ''}
                                  </div>
                                )}
                                {student.hasEHCP && student.ehcpDetails && (
                                  <div className="mt-2 pt-2 border-t border-gray-200">
                                    <div className="font-medium text-gray-700">EHCP Details:</div>
                                    <div className="text-xs">
                                      <div>Needs: {(student.ehcpDetails.needs || []).slice(0, 2).join(', ') || 'Not specified'}...</div>
                                      <div>Targets: {(student.ehcpDetails.targets || []).slice(0, 2).join(', ') || 'Not specified'}...</div>
                                      <div>Last Review: {formatDate(student.ehcpDetails.lastReview) || 'Not recorded'}</div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
        </div>
      </div>

      {/* Add Student Form */}
      <AddStudentForm
        open={showAddStudentForm}
        setOpen={setShowAddStudentForm}
        onStudentAdded={() => { /* Realtime via useStudents */ }}
      />
    </AppLayout>
  );
};

export default StudentOrganizationViewer;
